import "./style.scss";

import { memo, useCallback, useEffect, useRef, useState } from "react";
import { OverlayTrigger } from "react-bootstrap";
import { toast } from "react-toastify";

import { renderTooltip } from "../../../../components/StyledTooltip/StyledTooltip";
import type { Mentor } from "../../../../hooks/reactQueryHooks/useMentors";
import blackImage from "../images/black.jpeg";

interface MentorCardProps {
  profile: Mentor;
  onCardClick?: () => void;
}

export const MentorCard = memo(({ profile, onCardClick }: MentorCardProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleCopy = useCallback(
    async (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      try {
        // Use the simpler writeText API for clipboard copying
        await window.navigator.clipboard.writeText(profile.mentorId);
        setIsCopied(true);
        toast.success("Copied mentor ID to clipboard!");
        // Clear any existing timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        // Set a new timeout to reset state
        timeoutRef.current = setTimeout(() => {
          setIsCopied(false);
          timeoutRef.current = null;
        }, 2000);
      } catch (err) {
        console.error("Failed to copy to clipboard: ", err);
      }
    },
    [profile.mentorId],
  );

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleCardKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (onCardClick && (e.key === "Enter" || e.key === " ")) {
        onCardClick();
      }
    },
    [onCardClick],
  );

  const handleCopyKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter" || e.key === " ") {
        void handleCopy(e as unknown as React.MouseEvent<HTMLDivElement>);
      }
    },
    [handleCopy],
  );

  return (
    <div
      className="mentor-card"
      data-id={profile.mentorId}
      onClick={onCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={handleCardKeyDown}
    >
      <div className="card-bg-img">
        <video
          preload="metadata"
          src={profile?.videoLoopUrl}
          muted
          autoPlay
          loop
          playsInline
          poster={profile?.previewUrl || blackImage}
          onError={e => {
            e.currentTarget.poster = blackImage;
          }}
        />
      </div>
      <OverlayTrigger
        placement="bottom"
        overlay={renderTooltip(<span>Copy mentor ID</span>)}
      >
        <div
          className="card-copy-btn"
          data-clipboard-text={profile.mentorId}
          onClick={handleCopy}
          role="button"
          tabIndex={0}
          onKeyDown={handleCopyKeyDown}
        >
          {isCopied ? (
            <i
              className="fa-solid fa-clipboard-check copied"
              aria-label="Copied"
            />
          ) : (
            <i
              className="fa-regular fa-clipboard"
              aria-label="Copy to clipboard"
            />
          )}
        </div>
      </OverlayTrigger>
      <div className="avatar-details">
        <div className="avatar-name mb-2">
          {`${profile.firstName} ${profile.lastName}`}
        </div>
        <div className="avatar-headline mb-1" title={profile.headline}>
          {profile.headline}
        </div>
        <div className="align-items-center gap-2 flex-wrap professional-info">
          <div className="d-flex align-items-center gap-1">
            <i className="fa fa-briefcase" aria-hidden="true" />
            <span>{profile.jobTitle}</span>
          </div>
          <div className="rounded-circle divider-dot" aria-hidden="true" />
          <div className="d-flex align-items-center gap-1">
            <i className="fa fa-building" aria-hidden="true" />
            <span>{profile.company}</span>
          </div>
        </div>
      </div>
    </div>
  );
});
